<template>
  <div class="overview">
    <!-- KPI-uri principale -->
    <div class="ov-kpi-grid">
      <div class="ov-kpi-card ov-kpi-primary" :class="{ 'ov-kpi-loading': loading }">
        <div class="ov-kpi-top">
          <span class="ov-kpi-icon"><v-icon size="20">mdi-domain</v-icon></span>
          <span class="ov-kpi-label">Firme active</span>
        </div>
        <div class="ov-kpi-value">
          <span v-if="loading" class="ov-skeleton ov-skeleton-lg"></span>
          <template v-else>{{ firmeActive }}<span class="ov-kpi-of">/ {{ firmeTotal }}</span></template>
        </div>
        <div class="ov-kpi-bar" v-if="!loading">
          <div class="ov-kpi-bar-fill" :style="{ width: firmePct + '%' }"></div>
        </div>
        <span v-if="!loading" class="ov-kpi-sub">{{ firmePct }}% din firmele configurate</span>
      </div>

      <div class="ov-kpi-card" :class="{ 'ov-kpi-loading': loading }">
        <div class="ov-kpi-top">
          <span class="ov-kpi-icon ov-kpi-icon-grey"><v-icon size="20">mdi-account-box-multiple-outline</v-icon></span>
          <span class="ov-kpi-label">Conturi active</span>
        </div>
        <div class="ov-kpi-value">
          <span v-if="loading" class="ov-skeleton ov-skeleton-lg"></span>
          <template v-else>{{ accountsActive }}<span class="ov-kpi-of">/ {{ accountsTotal }}</span></template>
        </div>
        <div class="ov-kpi-bar" v-if="!loading">
          <div class="ov-kpi-bar-fill ov-kpi-bar-grey" :style="{ width: accountsActivePct + '%' }"></div>
        </div>
        <span v-if="!loading" class="ov-kpi-sub">{{ accountsInactive }} dezactivate</span>
      </div>

      <div class="ov-kpi-card" :class="{ 'ov-kpi-loading': loading }">
        <div class="ov-kpi-top">
          <span class="ov-kpi-icon ov-kpi-icon-super"><v-icon size="20">mdi-shield-crown-outline</v-icon></span>
          <span class="ov-kpi-label">Super administratori</span>
        </div>
        <div class="ov-kpi-value">
          <span v-if="loading" class="ov-skeleton ov-skeleton-lg"></span>
          <template v-else>{{ superAdmins }}</template>
        </div>
        <span v-if="!loading" class="ov-kpi-sub">din {{ accountsTotal }} conturi aplicație</span>
      </div>

      <div class="ov-kpi-card" :class="{ 'ov-kpi-loading': loading }">
        <div class="ov-kpi-top">
          <span class="ov-kpi-icon"><v-icon size="20">mdi-account-group</v-icon></span>
          <span class="ov-kpi-label">Acoperire conturi AD</span>
        </div>
        <div class="ov-kpi-value">
          <span v-if="loading" class="ov-skeleton ov-skeleton-lg"></span>
          <template v-else>{{ coveragePct }}<span class="ov-kpi-of">%</span></template>
        </div>
        <div class="ov-kpi-bar" v-if="!loading">
          <div class="ov-kpi-bar-fill" :style="{ width: coveragePct + '%' }"></div>
        </div>
        <span v-if="!loading" class="ov-kpi-sub">{{ usersCuCont }} din {{ usersTotal }} utilizatori AD au cont</span>
      </div>

      <div class="ov-kpi-card" :class="{ 'ov-kpi-loading': loading }">
        <div class="ov-kpi-top">
          <span class="ov-kpi-icon ov-kpi-icon-warn"><v-icon size="20">mdi-account-alert-outline</v-icon></span>
          <span class="ov-kpi-label">Conturi fără AD</span>
        </div>
        <div class="ov-kpi-value">
          <span v-if="loading" class="ov-skeleton ov-skeleton-lg"></span>
          <template v-else>{{ accountsWithoutAD.length }}</template>
        </div>
        <span v-if="!loading" class="ov-kpi-sub">necesită legare la Active Directory</span>
      </div>

      <div class="ov-kpi-card" :class="{ 'ov-kpi-loading': loading }">
        <div class="ov-kpi-top">
          <span class="ov-kpi-icon ov-kpi-icon-grey"><v-icon size="20">mdi-account-check-outline</v-icon></span>
          <span class="ov-kpi-label">Utilizatori AD activi</span>
        </div>
        <div class="ov-kpi-value">
          <span v-if="loading" class="ov-skeleton ov-skeleton-lg"></span>
          <template v-else>{{ usersTotal }}</template>
        </div>
        <span v-if="!loading" class="ov-kpi-sub">{{ usersFaraCont }} fără cont creat în aplicație</span>
      </div>
    </div>

    <div class="ov-columns">
      <!-- Firme -->
      <div class="ov-panel">
        <div class="ov-panel-head">
          <span class="ov-panel-title">Firme</span>
          <span class="ov-panel-count" v-if="!loading">{{ companies.length }}</span>
        </div>
        <div class="ov-panel-body">
          <template v-if="loading">
            <div v-for="n in 4" :key="n" class="ov-list-skel"></div>
          </template>
          <template v-else-if="companies.length">
            <div v-for="c in companies" :key="c.ID" class="ov-list-row">
              <div class="ov-mono">{{ initials(c.Name) }}</div>
              <div class="ov-list-main">
                <span class="ov-list-name">{{ c.Name }}</span>
                <span class="ov-list-meta">{{ c.Source || "—" }}</span>
              </div>
              <span class="ov-badge" :class="c.Active ? 'ov-badge-ok' : 'ov-badge-off'">
                {{ c.Active ? "Activă" : "Inactivă" }}
              </span>
            </div>
          </template>
          <div v-else class="ov-empty">Nu există firme configurate.</div>
        </div>
      </div>

      <!-- Acoperire AD -->
      <div class="ov-panel">
        <div class="ov-panel-head">
          <span class="ov-panel-title">Acoperire conturi AD</span>
        </div>
        <div class="ov-panel-body">
          <template v-if="loading">
            <div class="ov-list-skel"></div>
          </template>
          <template v-else>
            <div class="ov-coverage">
              <div class="ov-coverage-bar">
                <div class="ov-coverage-fill" :style="{ width: coveragePct + '%' }"></div>
              </div>
              <div class="ov-coverage-stats">
                <span class="ov-coverage-pct">{{ coveragePct }}%</span>
                <span class="ov-coverage-label">din utilizatorii AD au cont creat în aplicație</span>
              </div>
            </div>
            <div class="ov-legend">
              <div class="ov-legend-item">
                <span class="ov-dot ov-dot-red"></span>
                <span>{{ usersCuCont }} cu cont</span>
              </div>
              <div class="ov-legend-item">
                <span class="ov-dot ov-dot-grey"></span>
                <span>{{ usersFaraCont }} fără cont</span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Conturi fara AD -->
      <div class="ov-panel">
        <div class="ov-panel-head">
          <span class="ov-panel-title">Conturi fără legătură AD</span>
          <span class="ov-panel-count" v-if="!loading">{{ accountsWithoutAD.length }}</span>
        </div>
        <div class="ov-panel-body">
          <template v-if="loading">
            <div v-for="n in 3" :key="n" class="ov-list-skel"></div>
          </template>
          <template v-else-if="accountsWithoutAD.length">
            <div v-for="a in accountsWithoutAD" :key="a.ID" class="ov-list-row">
              <div class="ov-mono ov-mono-alt">{{ initials(a.Username) }}</div>
              <div class="ov-list-main">
                <span class="ov-list-name">{{ a.Username }}</span>
                <span class="ov-list-meta">{{ a.Email || "—" }}</span>
              </div>
              <span v-if="a.SuperAdmin" class="ov-badge ov-badge-super">Super admin</span>
              <span class="ov-badge" :class="a.Active ? 'ov-badge-ok' : 'ov-badge-off'">
                {{ a.Active ? "Activ" : "Inactiv" }}
              </span>
            </div>
          </template>
          <div v-else class="ov-empty">Toate conturile sunt legate de un cont AD.</div>
        </div>
      </div>
    </div>

    <!-- Donuts + Atentionari -->
    <div class="ov-columns ov-columns-2">
      <div class="ov-panel">
        <div class="ov-panel-head">
          <span class="ov-panel-title">Distribuții</span>
        </div>
        <div class="ov-panel-body ov-donuts-body">
          <template v-if="loading">
            <div v-for="n in 3" :key="n" class="ov-donut-skel"></div>
          </template>
          <template v-else>
            <div v-for="d in donuts" :key="d.key" class="ov-donut-block">
              <div class="ov-donut" :style="{ background: d.gradient }">
                <span class="ov-donut-pct">{{ d.pct }}%</span>
              </div>
              <div class="ov-donut-legend">
                <span class="ov-donut-title">{{ d.title }}</span>
                <div class="ov-legend-item">
                  <span class="ov-dot ov-dot-red"></span>
                  <span>{{ d.value1 }} {{ d.label1 }}</span>
                </div>
                <div class="ov-legend-item">
                  <span class="ov-dot ov-dot-grey"></span>
                  <span>{{ d.value2 }} {{ d.label2 }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="ov-panel">
        <div class="ov-panel-head">
          <span class="ov-panel-title">Atenționări</span>
          <span class="ov-panel-count" v-if="!loading && alerts.length">{{ alerts.length }}</span>
        </div>
        <div class="ov-panel-body">
          <template v-if="loading">
            <div v-for="n in 3" :key="n" class="ov-list-skel"></div>
          </template>
          <template v-else-if="alerts.length">
            <div v-for="(a, i) in alerts" :key="i" class="ov-alert-row" :class="'ov-alert-' + a.tone">
              <v-icon size="18">{{ a.icon }}</v-icon>
              <span>{{ a.text }}</span>
            </div>
          </template>
          <div v-else class="ov-empty ov-empty-ok">
            <v-icon size="18" color="#059669">mdi-check-circle-outline</v-icon>
            <span>Nimic de semnalat.</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Stare module -->
    <div class="ov-panel ov-log-panel">
      <div class="ov-panel-head">
        <span class="ov-panel-title">Stare module</span>
        <div class="ov-modules-head-right">
          <span class="ov-modules-summary" v-if="!modulesChecking">{{ modulesUpCount }}/{{ modules.length }} module online</span>
          <button class="ov-refresh-btn" type="button" :disabled="modulesChecking" @click="checkAllModules">
            <v-icon size="15">mdi-refresh</v-icon>
            <span>Reîmprospătează</span>
          </button>
        </div>
      </div>
      <div class="ov-panel-body ov-modules-body">
        <div v-for="m in modules" :key="m.name" class="ov-module-row">
          <span
            class="ov-mod-dot"
            :class="{
              'ov-mod-dot-up': m.status === 'up',
              'ov-mod-dot-down': m.status === 'down',
              'ov-mod-dot-checking': m.status === 'checking',
            }"
          ></span>
          <span class="ov-mod-label">{{ m.label }}</span>
          <span v-if="m.status === 'checking'" class="ov-mod-skel"></span>
          <span v-else-if="m.status === 'up'" class="ov-mod-time">{{ m.responseTimeMs }} ms</span>
          <span v-else class="ov-mod-down-text">indisponibil</span>
        </div>
      </div>
    </div>

    <div class="ov-note">
      <v-icon size="14">mdi-information-outline</v-icon>
      <span>Datele despre utilizatori vin din Active Directory (utilizatori activi, cu login în ultimele 3 luni).</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import ADServices from "@/services/AUTH/AD_Services";
import UserAdminService from "@/services/AUTH/UserAdmin";
import AppSettings from "@/settings/appSettings";

interface ModuleHealth {
  name: string;
  label: string;
  url: string;
  status: "checking" | "up" | "down";
  responseTimeMs: number | null;
  lastChecked: Date | null;
}

/**
 * Deriva URL-ul de health-check dintr-un URL de API configurat, eliminand
 * segmentul final de tip /api/, /v1/ sau /odata/ si adaugand /health.
 * Fallback: origin-ul URL-ului + /health, daca parsarea nu reuseste.
 */
function toHealthUrl(apiUrl: string): string | null {
  if (!apiUrl) return null;
  try {
    const trimmed = apiUrl.replace(/\/(api|v1|odata)\/?$/i, "/");
    const url = new URL(trimmed);
    return `${url.origin}/health`;
  } catch {
    try {
      const url = new URL(apiUrl);
      return `${url.origin}/health`;
    } catch {
      return null;
    }
  }
}

interface CompanyRow {
  ID: string;
  Name: string;
  Source?: string;
  Active: boolean;
}

interface ADUserRow {
  DisplayName?: string;
  SamAccountName?: string;
  Email?: string;
  AreCont: boolean;
}

interface AccountRow {
  ID: string;
  Username: string;
  Email?: string;
  Active: boolean;
  SuperAdmin: boolean;
  ADAccount: boolean;
}

export default defineComponent({
  name: "AdminOverview",
  setup() {
    const loading = ref(true);

    const companies = ref<CompanyRow[]>([]);
    const adUsers = ref<ADUserRow[]>([]);
    const accounts = ref<AccountRow[]>([]);

    const firmeTotal = computed(() => companies.value.length);
    const firmeActive = computed(() => companies.value.filter((c) => c.Active).length);
    const firmeInactive = computed(() => firmeTotal.value - firmeActive.value);
    const firmePct = computed(() => (firmeTotal.value > 0 ? Math.round((firmeActive.value / firmeTotal.value) * 100) : 0));

    const usersTotal = computed(() => adUsers.value.length);
    const usersCuCont = computed(() => adUsers.value.filter((u) => u.AreCont).length);
    const usersFaraCont = computed(() => usersTotal.value - usersCuCont.value);
    const coveragePct = computed(() =>
      usersTotal.value > 0 ? Math.round((usersCuCont.value / usersTotal.value) * 100) : 0
    );

    const accountsTotal = computed(() => accounts.value.length);
    const accountsActive = computed(() => accounts.value.filter((a) => a.Active).length);
    const accountsInactive = computed(() => accountsTotal.value - accountsActive.value);
    const accountsActivePct = computed(() =>
      accountsTotal.value > 0 ? Math.round((accountsActive.value / accountsTotal.value) * 100) : 0
    );
    const superAdmins = computed(() => accounts.value.filter((a) => a.SuperAdmin).length);
    const accountsWithoutAD = computed(() => accounts.value.filter((a) => !a.ADAccount));
    const accountsWithAD = computed(() => accountsTotal.value - accountsWithoutAD.value.length);

    const firmeFaraSource = computed(() => companies.value.filter((c) => !c.Source).length);

    const donutGradient = (pct: number) =>
      `conic-gradient(#ff2c21 0% ${pct}%, #eef1f6 ${pct}% 100%)`;

    const donuts = computed(() => {
      const firmePctLocal = firmeTotal.value > 0 ? Math.round((firmeActive.value / firmeTotal.value) * 100) : 0;
      const contPct = accountsTotal.value > 0 ? Math.round((accountsActive.value / accountsTotal.value) * 100) : 0;
      const adPct = accountsTotal.value > 0 ? Math.round((accountsWithAD.value / accountsTotal.value) * 100) : 0;
      return [
        {
          key: "firme",
          title: "Firme",
          pct: firmePctLocal,
          gradient: donutGradient(firmePctLocal),
          value1: firmeActive.value,
          label1: "active",
          value2: firmeInactive.value,
          label2: "inactive",
        },
        {
          key: "conturi",
          title: "Conturi aplicație",
          pct: contPct,
          gradient: donutGradient(contPct),
          value1: accountsActive.value,
          label1: "active",
          value2: accountsInactive.value,
          label2: "inactive",
        },
        {
          key: "ad",
          title: "Conturi cu AD",
          pct: adPct,
          gradient: donutGradient(adPct),
          value1: accountsWithAD.value,
          label1: "cu AD",
          value2: accountsWithoutAD.value.length,
          label2: "fără AD",
        },
      ];
    });

    const alerts = computed(() => {
      const list: { tone: "warn" | "red"; icon: string; text: string }[] = [];
      if (firmeFaraSource.value > 0) {
        list.push({
          tone: "warn",
          icon: "mdi-cable-data",
          text: `${firmeFaraSource.value} firme fără conexiune configurată`,
        });
      }
      if (firmeInactive.value > 0) {
        list.push({ tone: "warn", icon: "mdi-domain-off", text: `${firmeInactive.value} firme inactive` });
      }
      if (accountsInactive.value > 0) {
        list.push({ tone: "warn", icon: "mdi-account-off-outline", text: `${accountsInactive.value} conturi dezactivate` });
      }
      if (accountsWithoutAD.value.length > 0) {
        list.push({
          tone: "warn",
          icon: "mdi-account-alert-outline",
          text: `${accountsWithoutAD.value.length} conturi fără AD`,
        });
      }
      if (usersFaraCont.value > 0) {
        list.push({
          tone: "red",
          icon: "mdi-account-question-outline",
          text: `${usersFaraCont.value} utilizatori AD fără cont`,
        });
      }
      return list;
    });

    const initials = (name?: string) => {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) return "?";
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    };

    const load = async () => {
      loading.value = true;
      try {
        const [companiesRes, usersRes, accountsRes] = await Promise.allSettled([
          ADServices.getCompanies(),
          ADServices.getUsers(),
          UserAdminService.getAll(),
        ]);
        if (companiesRes.status === "fulfilled") {
          companies.value = companiesRes.value.data || [];
        }
        if (usersRes.status === "fulfilled") {
          adUsers.value = usersRes.value.data || [];
        }
        if (accountsRes.status === "fulfilled") {
          accounts.value = accountsRes.value.data || [];
        }
      } finally {
        loading.value = false;
      }
    };

    // EdiIntegration si PortalHR au setari (API_Url_Edi, API_Url_PortalHR) -> incluse.
    // Toate cele 9 module au setare corespunzatoare in AppSettings, niciun modul nu e omis.
    const moduleDefs: { name: string; label: string; apiUrl: string }[] = [
      { name: "Auth", label: "Autentificare", apiUrl: AppSettings.API_Url },
      { name: "Comercial", label: "Comercial", apiUrl: AppSettings.API_Url_Comercial },
      { name: "Documente", label: "Documente", apiUrl: AppSettings.API_Url_Documente },
      { name: "Rapoarte", label: "Rapoarte", apiUrl: AppSettings.API_Url_Rapoarte },
      { name: "Contabilitate", label: "Contabilitate", apiUrl: AppSettings.API_Url_Contabilitate },
      { name: "ResurseUmane", label: "Resurse Umane", apiUrl: AppSettings.API_Url_ResurseUmane },
      { name: "Financiar", label: "Financiar", apiUrl: AppSettings.API_Url_Financiar },
      { name: "EdiIntegration", label: "EDI Integration", apiUrl: AppSettings.API_Url_Edi },
      { name: "PortalHR", label: "Portal HR", apiUrl: AppSettings.API_Url_PortalHR },
    ];

    const modules = ref<ModuleHealth[]>(
      moduleDefs
        .map((def) => ({ def, url: toHealthUrl(def.apiUrl) }))
        .filter((x) => !!x.url)
        .map((x) => ({
          name: x.def.name,
          label: x.def.label,
          url: x.url as string,
          status: "checking" as const,
          responseTimeMs: null,
          lastChecked: null,
        }))
    );

    const modulesChecking = computed(() => modules.value.some((m) => m.status === "checking"));
    const modulesUpCount = computed(() => modules.value.filter((m) => m.status === "up").length);

    const checkModule = async (m: ModuleHealth) => {
      m.status = "checking";
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 4000);
      const start = performance.now();
      try {
        await fetch(m.url, { signal: controller.signal });
        m.status = "up";
        m.responseTimeMs = Math.round(performance.now() - start);
      } catch {
        m.status = "down";
        m.responseTimeMs = null;
      } finally {
        clearTimeout(timeout);
        m.lastChecked = new Date();
      }
    };

    const checkAllModules = async () => {
      await Promise.allSettled(modules.value.map((m) => checkModule(m)));
    };

    onMounted(() => {
      load();
      checkAllModules();
    });

    return {
      loading,
      companies,
      accounts,
      usersCuCont,
      usersFaraCont,
      coveragePct,
      accountsWithoutAD,
      initials,
      firmeTotal,
      firmeActive,
      firmePct,
      accountsTotal,
      accountsActive,
      accountsInactive,
      accountsActivePct,
      superAdmins,
      usersTotal,
      donuts,
      alerts,
      modules,
      modulesChecking,
      modulesUpCount,
      checkAllModules,
    };
  },
});
</script>

<style scoped>
.overview { padding: 4px; }

.ov-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 16px;
}
.ov-kpi-card {
  background: #ffffff;
  border: 1px solid #eef1f6;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ov-kpi-primary { border-color: rgba(255, 44, 33, 0.18); }

.ov-kpi-top { display: flex; align-items: center; gap: 10px; }
.ov-kpi-icon {
  width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255, 44, 33, 0.1); color: #ff2c21;
}
.ov-kpi-icon-grey { background: #f1f5f9; color: #64748b; }
.ov-kpi-icon-super { background: rgba(255, 44, 33, 0.12); color: #ff2c21; }
.ov-kpi-icon-warn { background: #fef3c7; color: #92400e; }

.ov-kpi-label { font-size: 11.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; color: #94a3b8; }

.ov-kpi-value { font-size: 30px; font-weight: 800; color: #1e293b; line-height: 1; font-variant-numeric: tabular-nums; }
.ov-kpi-of { font-size: 16px; font-weight: 600; color: #94a3b8; margin-left: 4px; }

.ov-kpi-bar { width: 100%; height: 6px; border-radius: 6px; background: #f1f5f9; overflow: hidden; }
.ov-kpi-bar-fill { height: 100%; border-radius: 6px; background: linear-gradient(90deg, #ff2c21, #ff6a4a); transition: width 0.4s ease; }
.ov-kpi-bar-grey .ov-kpi-bar-fill,
.ov-kpi-bar-grey { background: #cbd5e1; }

.ov-kpi-sub { font-size: 11.5px; color: #94a3b8; }

.ov-kpi-loading .ov-kpi-value { min-height: 30px; display: flex; align-items: center; }

.ov-skeleton {
  display: inline-block;
  width: 40px; height: 22px; border-radius: 6px;
  background: linear-gradient(90deg, #eef1f6 25%, #f6f8fb 50%, #eef1f6 75%);
  background-size: 200% 100%;
  animation: ov-pulse 1.2s infinite;
}
.ov-skeleton-lg { width: 64px; height: 28px; }
@keyframes ov-pulse { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

.ov-columns {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 18px;
}
@media (max-width: 1100px) {
  .ov-columns { grid-template-columns: 1fr; }
}

.ov-panel {
  background: #ffffff;
  border: 1px solid #eef1f6;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ov-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  background: #fcfcfd;
}
.ov-panel-title { font-size: 13px; font-weight: 700; color: #1e293b; }
.ov-panel-count {
  font-size: 12px; font-weight: 700; color: #ff2c21;
  background: rgba(255, 44, 33, 0.08);
  border-radius: 10px; padding: 2px 9px;
}
.ov-panel-body { padding: 10px 12px; max-height: 320px; overflow-y: auto; flex: 1; }

.ov-list-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 6px;
  border-bottom: 1px solid #f6f8fb;
}
.ov-list-row:last-child { border-bottom: none; }

.ov-mono {
  width: 32px; height: 32px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 800; letter-spacing: 0.3px;
  background: rgba(255, 44, 33, 0.1); color: #ff2c21;
  flex-shrink: 0;
}
.ov-mono-alt { background: #f1f5f9; color: #64748b; }

.ov-list-main { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.ov-list-name { font-size: 13px; font-weight: 600; color: #334155; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ov-list-meta { font-size: 11px; color: #94a3b8; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.ov-badge {
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  flex-shrink: 0;
  white-space: nowrap;
}
.ov-badge-ok { background: #ecfdf5; color: #059669; }
.ov-badge-off { background: #f1f5f9; color: #94a3b8; }
.ov-badge-super { background: rgba(255, 44, 33, 0.1); color: #ff2c21; margin-right: 6px; }

.ov-empty { padding: 18px 6px; text-align: center; font-size: 12.5px; color: #94a3b8; }

.ov-list-skel {
  height: 40px; border-radius: 8px; margin-bottom: 8px;
  background: linear-gradient(90deg, #eef1f6 25%, #f6f8fb 50%, #eef1f6 75%);
  background-size: 200% 100%;
  animation: ov-pulse 1.2s infinite;
}

.ov-coverage { padding: 6px 6px 14px; }
.ov-coverage-bar {
  width: 100%; height: 12px; border-radius: 8px;
  background: #f1f5f9; overflow: hidden;
}
.ov-coverage-fill {
  height: 100%; border-radius: 8px;
  background: linear-gradient(90deg, #ff2c21, #ff6a4a);
  transition: width 0.4s ease;
}
.ov-coverage-stats { display: flex; align-items: baseline; gap: 8px; margin-top: 10px; }
.ov-coverage-pct { font-size: 24px; font-weight: 800; color: #1e293b; }
.ov-coverage-label { font-size: 12px; color: #94a3b8; }

.ov-legend { display: flex; gap: 18px; padding: 0 6px 6px; }
.ov-legend-item { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: #334155; }
.ov-dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
.ov-dot-red { background: #ff2c21; }
.ov-dot-grey { background: #cbd5e1; }

.ov-note {
  display: flex; align-items: center; gap: 8px;
  margin-top: 18px; font-size: 12.5px; color: #94a3b8;
}

.ov-columns-2 {
  grid-template-columns: 1.1fr 1fr;
}
@media (max-width: 1100px) {
  .ov-columns-2 { grid-template-columns: 1fr; }
}

.ov-donuts-body { display: flex; flex-wrap: wrap; gap: 18px; padding: 16px 12px; }
.ov-donut-block { display: flex; align-items: center; gap: 14px; flex: 1 1 200px; }
.ov-donut {
  width: 76px; height: 76px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  position: relative;
}
.ov-donut::before {
  content: ""; position: absolute; inset: 11px;
  background: #ffffff; border-radius: 50%;
}
.ov-donut-pct { position: relative; z-index: 1; font-size: 14px; font-weight: 800; color: #1e293b; }
.ov-donut-legend { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.ov-donut-title { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 2px; }

.ov-donut-skel {
  width: 76px; height: 76px; border-radius: 50%;
  background: linear-gradient(90deg, #eef1f6 25%, #f6f8fb 50%, #eef1f6 75%);
  background-size: 200% 100%;
  animation: ov-pulse 1.2s infinite;
}

.ov-alert-row {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 10px; border-radius: 10px;
  font-size: 12.5px; margin-bottom: 6px;
}
.ov-alert-row:last-child { margin-bottom: 0; }
.ov-alert-warn { background: #f8f9fc; color: #475569; }
.ov-alert-warn .v-icon { color: #94a3b8; }
.ov-alert-red { background: rgba(255, 44, 33, 0.06); color: #b91c1c; }
.ov-alert-red .v-icon { color: #ff2c21; }

.ov-empty-ok { display: flex; align-items: center; justify-content: center; gap: 8px; color: #059669; }

.ov-log-panel { margin-top: 16px; }

.ov-modules-head-right { display: flex; align-items: center; gap: 14px; }
.ov-modules-summary { font-size: 12px; font-weight: 700; color: #059669; }
.ov-refresh-btn {
  display: flex; align-items: center; gap: 5px;
  font-size: 11.5px; font-weight: 600; color: #475569;
  background: transparent; border: 1px solid #e5e7eb; border-radius: 8px;
  padding: 5px 10px; cursor: pointer; transition: background 0.15s ease;
}
.ov-refresh-btn:hover:not(:disabled) { background: #f8f9fc; }
.ov-refresh-btn:disabled { opacity: 0.6; cursor: default; }

.ov-modules-body {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 4px 18px;
  max-height: none;
  padding: 12px 16px;
}
.ov-module-row {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 0; border-bottom: 1px solid #f6f8fb;
}
.ov-mod-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.ov-mod-dot-up { background: #059669; }
.ov-mod-dot-down { background: #ff2c21; }
.ov-mod-dot-checking { background: #cbd5e1; animation: ov-pulse-dot 1s infinite; }
@keyframes ov-pulse-dot { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }

.ov-mod-label { font-size: 12.5px; font-weight: 600; color: #334155; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ov-mod-time { font-size: 11.5px; font-weight: 700; color: #059669; font-variant-numeric: tabular-nums; }
.ov-mod-down-text { font-size: 11.5px; font-weight: 700; color: #ff2c21; }
.ov-mod-skel {
  width: 44px; height: 14px; border-radius: 5px;
  background: linear-gradient(90deg, #eef1f6 25%, #f6f8fb 50%, #eef1f6 75%);
  background-size: 200% 100%;
  animation: ov-pulse 1.2s infinite;
}
</style>
